import { useMemo, useState } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { AptSummaryCard } from "src/components/apt-summary-card";
import { ChartBuilder } from "src/utils/data-convert";
import { DateTypes, ChartType } from "src/utils/configs";
import { getDataRequest } from "src/utils/data";
import { useDataLake } from "src/hooks/use-datalake";
import { getTypeColor } from "src/utils/util";
import {
  addMarketPriceAxes,
  MARKET_PRICE_AXIS,
} from "./apt-chart-axes";

/** 카드용 컴팩트 Highcharts 옵션으로 변환 */
const toCompactOption = (opts, height) => {
  if (!opts) return null;
  opts.chart = { ...opts.chart, height, margin: [10, 50, 35, 50], backgroundColor: "transparent" };
  opts.title = { text: null };
  opts.credits = { enabled: false };
  opts.legend = { enabled: false };
  opts.tooltip = {
    ...opts.tooltip,
    enabled: true,
    shared: true,
    crosshairs: true,
    outside: false,
  };
  opts.plotOptions = {
    ...opts.plotOptions,
    series: {
      ...opts.plotOptions?.series,
      stickyTracking: true,
      states: {
        ...opts.plotOptions?.series?.states,
        hover: {
          ...opts.plotOptions?.series?.states?.hover,
          enabled: true,
          lineWidthPlus: 1,
        },
      },
      marker: {
        ...opts.plotOptions?.series?.marker,
        enabled: false,
        states: {
          ...opts.plotOptions?.series?.marker?.states,
          hover: {
            ...opts.plotOptions?.series?.marker?.states?.hover,
            enabled: true,
            radius: 4,
          },
        },
      },
    },
    scatter: {
      ...opts.plotOptions?.scatter,
      marker: {
        ...opts.plotOptions?.scatter?.marker,
        enabled: true,
        radius: 2.5,
        states: {
          ...opts.plotOptions?.scatter?.marker?.states,
          hover: {
            ...opts.plotOptions?.scatter?.marker?.states?.hover,
            enabled: true,
            radius: 4,
          },
        },
      },
    },
  };
  if (opts.xAxis) opts.xAxis.labels = { ...opts.xAxis.labels, style: { fontSize: "8px" } };
  const yArr = Array.isArray(opts.yAxis) ? opts.yAxis : [opts.yAxis];
  yArr.forEach((y) => {
    if (y) { y.labels = { ...y.labels, style: { fontSize: "8px" } }; y.title = { text: null }; }
  });
  return opts;
};

/**
 * 아파트 평형의 카드용 실거래/매물시세 차트 옵션을 만든다.
 * 관심아파트 카드와 신고가 카드가 같은 데이터 요청·차트 스타일을 공유한다.
 */
export const useAptChartOptions = (
  pyeongId,
  enabled = true,
  height = 140,
  chartMode = "all",
  marketPyeongId = null,
  dataRange = "SP",
) => {
  const requests = useMemo(() => {
    if (!pyeongId || !enabled) return {};
    const includeRealPrice = chartMode !== "market";
    const includeMarketPrice = chartMode !== "realprice";
    // 급매는 공급평형 실거래와 실제 판단에 사용한 detail 평형 호가를 함께 비교한다.
    const marketTargetPyeongId = marketPyeongId || pyeongId;
    const marketDataRange = marketPyeongId ? "P" : dataRange;
    const target = dataRange === "A" ? { aptId: pyeongId } : { pyeongId };
    const marketTarget = marketPyeongId ? { pyeongId: marketTargetPyeongId } : target;
    const nextRequests = {};

    if (includeRealPrice) {
      // 실거래 월별 (LINE)
      nextRequests.D_RP_M = getDataRequest("D", "RP", "M", dataRange, "MST", target);
      nextRequests.L_RP_M = getDataRequest("L", "RP", "M", dataRange, "MST", target);
      // 실거래 일별 (SCATTER 점)
      nextRequests.D_RP_D = getDataRequest("D", "RP", "D", dataRange, "MST", target);
      nextRequests.L_RP_D = getDataRequest("L", "RP", "D", dataRange, "MST", target);
    }

    if (includeMarketPrice) {
      // 매물시세 (LINE)
      nextRequests.D_MP = getDataRequest(
        "D", "MP", "D", marketDataRange, "ONN", marketTarget,
      );
      nextRequests.L_MP = getDataRequest(
        "L", "MP", "D", marketDataRange, "ONN", marketTarget,
      );
      // 매물수 (AREA)
      nextRequests.D_SC = getDataRequest(
        "D", "SC", "D", marketDataRange, "ONN", marketTarget,
      );
      nextRequests.L_SC = getDataRequest(
        "L", "SC", "D", marketDataRange, "ONN", marketTarget,
      );
    }

    return nextRequests;
  }, [pyeongId, enabled, chartMode, marketPyeongId, dataRange]);
  const requestList = useMemo(() => Object.values(requests), [requests]);

  const { initialized, responses } = useDataLake(requestList);

  const { rpOption, mpOption } = useMemo(() => {
    if (!initialized || requestList.length === 0 || !responses.hasResponse())
      return { rpOption: null, mpOption: null };

    const D_RP_M = requests.D_RP_M ? responses.getResponse(requests.D_RP_M) : null;
    const L_RP_M = requests.L_RP_M ? responses.getResponse(requests.L_RP_M) : null;
    const D_RP_D = requests.D_RP_D ? responses.getResponse(requests.D_RP_D) : null;
    const L_RP_D = requests.L_RP_D ? responses.getResponse(requests.L_RP_D) : null;
    const D_MP = requests.D_MP ? responses.getResponse(requests.D_MP) : null;
    const L_MP = requests.L_MP ? responses.getResponse(requests.L_MP) : null;
    const D_SC = requests.D_SC ? responses.getResponse(requests.D_SC) : null;
    const L_SC = requests.L_SC ? responses.getResponse(requests.L_SC) : null;

    // --- 실거래 차트: LINE(월별) + SCATTER(일별 점) ---
    let rpOpt = null;
    if (D_RP_M?.data?.length > 0 || L_RP_M?.data?.length > 0) {
      const builder = new ChartBuilder("실거래", DateTypes.MONTHLY, undefined, { xTickDays: 365 });
      builder.addNumericYAxis("금액");

      // 일별 실거래 점 (SCATTER) — 카드용으로 작게
      [D_RP_D, L_RP_D].forEach((rp) => {
        if (rp?.data?.length > 0) {
          builder.addYValue(rp.data[0], rp.dates, {
            yAxis: 0,
            name: rp.displayName,
            chartType: ChartType.SCATTER,
            color: getTypeColor(rp.request.dataType, 0.3),
            markerSize: 1.5,
            label: false,
            magnification: rp.data[0].metadata?.dataInfo?.magnification ?? 1,
          });
        }
      });

      // 월별 실거래 LINE
      [D_RP_M, L_RP_M].forEach((rp) => {
        if (rp?.data?.length > 0) {
          builder.addYValue(rp.data[0], rp.dates, {
            yAxis: 0,
            name: rp.displayName,
            chartType: ChartType.LINE,
            color: getTypeColor(rp.request.dataType, 1),
            label: false,
            lastLabel: false,
            magnification: rp.data[0].metadata?.dataInfo?.magnification ?? 1,
          });
        }
      });

      rpOpt = toCompactOption(builder.build(), height);
    }

    // --- 매물시세 차트: 매매/전세 시세(LINE) + 매물수(AREA) ---
    let mpOpt = null;
    if (D_MP?.data?.length > 0 || L_MP?.data?.length > 0) {
      const builder = new ChartBuilder("매물시세", DateTypes.DAILY, undefined, { xTickDays: 365 });
      // 매매·전세시세는 같은 금액축에서 비교하고 매물수만 숨김 보조축을 사용한다.
      addMarketPriceAxes(builder);

      // 매물수 AREA (시세보다 뒤에 그려지도록 먼저 추가)
      if (D_SC?.data?.length > 0) {
        builder.addYValue(D_SC.data[0], D_SC.dates, {
          yAxis: MARKET_PRICE_AXIS.SALE_COUNT,
          name: "매매매물",
          chartType: ChartType.AREA,
          color: "rgba(21, 101, 192, 0.25)",
          label: false,
          lastLabel: false,
          lineWidth: 0,
        });
      }
      if (L_SC?.data?.length > 0) {
        builder.addYValue(L_SC.data[0], L_SC.dates, {
          yAxis: MARKET_PRICE_AXIS.SALE_COUNT,
          name: "전세매물",
          chartType: ChartType.AREA,
          color: "rgba(229, 57, 53, 0.25)",
          label: false,
          lastLabel: false,
          lineWidth: 0,
        });
      }

      // 매물시세 LINE
      if (D_MP?.data?.length > 0) {
        builder.addYValue(D_MP.data[0], D_MP.dates, {
          yAxis: MARKET_PRICE_AXIS.PRICE,
          name: "매매시세",
          chartType: ChartType.LINE,
          color: "#1565C0",
          label: false,
          lastLabel: false,
          magnification: D_MP.data[0].metadata?.dataInfo?.magnification ?? 1,
        });
      }
      if (L_MP?.data?.length > 0) {
        builder.addYValue(L_MP.data[0], L_MP.dates, {
          yAxis: MARKET_PRICE_AXIS.PRICE,
          name: "전세시세",
          chartType: ChartType.LINE,
          color: "#E53935",
          label: false,
          lastLabel: false,
          magnification: L_MP.data[0].metadata?.dataInfo?.magnification ?? 1,
        });
      }

      mpOpt = toCompactOption(builder.build(), height);
    }

    return { rpOption: rpOpt, mpOption: mpOpt };
  }, [initialized, responses, requests, requestList.length, height]);

  // 요청 키가 Redux DataLake 상태에 모두 들어오기 전까지 빈 차트로 판단하지 않는다.
  const loading = enabled && requestList.length > 0 && !initialized;
  return { rpOption, mpOption, loading };
};

/**
 * 관심 아파트 카드 + 차트 (실거래 + 매물시세).
 * pyeongs-prices-panel.js 패턴 참고.
 *
 * @param {object} apt
 * @param {function} onClick
 * @param {boolean} showChart - true(기본): 차트 항상 표시 / false: "차트보기" 버튼으로 토글
 * @param {boolean} fullWidth - true: 부모 폭 100% (다이얼로그/세로 리스트용)
 */
export const AptChartCard = ({ apt, onClick, showChart = true, fullWidth = false, showStar = true }) => {
  const pyeongId = apt.pyeongId;
  // showChart=false일 때 사용자가 "차트보기"로 펼쳤는지 여부
  const [chartExpanded, setChartExpanded] = useState(false);
  // 실제 차트를 표시(데이터 fetch 포함)하는지 — showChart=true이거나 사용자가 펼친 경우
  const renderChart = showChart || chartExpanded;
  const { rpOption, mpOption } = useAptChartOptions(pyeongId, renderChart, 140);

  const chartContent = (rpOption || mpOption) ? (
    <>
      {rpOption && (
        <>
          <Divider sx={{ my: 0.3 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, fontWeight: 600 }}>실거래</Typography>
          <Box sx={{ width: "100%" }}>
            <HighchartsReact highcharts={Highcharts} options={rpOption} containerProps={{ style: { height: "140px" } }} />
          </Box>
        </>
      )}
      {mpOption && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, fontWeight: 600 }}>매물시세</Typography>
          <Box sx={{ width: "100%" }}>
            <HighchartsReact highcharts={Highcharts} options={mpOption} containerProps={{ style: { height: "140px" } }} />
          </Box>
        </>
      )}
    </>
  ) : null;

  // showChart=true: 차트 그대로
  // showChart=false: "차트 보기/숨기기" 토글 버튼 + 펼쳐졌을 때만 차트
  let chart;
  if (showChart) {
    chart = chartContent;
  } else {
    chart = (
      <>
        <Divider sx={{ my: 0.3 }} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.3}
          onClick={(e) => { e.stopPropagation(); setChartExpanded((v) => !v); }}
          sx={{
            py: 0.5,
            cursor: "pointer",
            borderRadius: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary" }}>
            {chartExpanded ? "차트 숨기기" : "차트 보기"}
          </Typography>
          {chartExpanded ? <MdExpandLess size={14} /> : <MdExpandMore size={14} />}
        </Stack>
        {chartExpanded && chartContent}
      </>
    );
  }

  return (
    <AptSummaryCard apt={apt} onClick={onClick} chart={chart} fullWidth={fullWidth} showStar={showStar} />
  );
};
